/**
 * Workbook import (FR-IMP-01 to FR-IMP-19).
 *
 * Pure: takes rows already read out of the spreadsheet and produces a plan — what would
 * be created, and what needs a human. The spreadsheet parsing itself is in the UI layer,
 * because it needs a file reader.
 *
 * Everything here was derived from the actual workbook rather than assumed, and verified
 * against exceljs rather than a hand-rolled XML reader. The counts in the tests are real:
 * 641 rows, 637 importable, four undated August rows, six with no account, and two
 * February-dated rows sitting on the January sheet.
 */

import { evaluateExpression } from './expression';
import { isValidDateKey, type DateKey } from './dates';
import type { Paise } from './money';
import type { TransactionInput, TransactionType } from './transactions';
import { MAHINDRA_CAR_LOAN, MAHINDRA_SCHEDULE, emiSplitOn } from './loans';

export interface RawRow {
  sheet: string;
  rowNumber: number;
  /** Column A. Blank on 6 of 641 rows. */
  account: string | null;
  /** Column B. Preserved verbatim into the description (FR-IMP-06). */
  description: string | null;
  /** Column C. May be a number or an arithmetic expression (FR-IMP-08). */
  amount: string | number | null;
  /** Column D, as an Excel serial. */
  dateSerial: number | null;
}

export type IssueKind =
  | 'missing-account'
  | 'missing-amount'
  | 'invalid-amount'
  | 'missing-date'
  | 'invalid-date'
  | 'unmapped-account'
  | 'duplicate'
  /** An EMI-shaped row with no matching instalment in the lender's schedule. */
  | 'unmatched-emi';

export interface ImportIssue {
  kind: IssueKind;
  sheet: string;
  rowNumber: number;
  message: string;
  /** What the row actually said, so the review queue can show it. */
  raw: { account: string | null; description: string | null; amount: string; date: string };
}

export interface PlannedTransaction {
  input: TransactionInput;
  sheet: string;
  rowNumber: number;
  issues: IssueKind[];
  /** Which reclassification rule matched, for the import summary. */
  rule: string | null;
}

export interface ImportPlan {
  transactions: PlannedTransaction[];
  issues: ImportIssue[];
  summary: {
    rowsRead: number;
    importable: number;
    flagged: number;
    skipped: number;
    byMonth: Record<string, number>;
    byRule: Record<string, number>;
    crossSheetDates: number;
  };
}

/**
 * Account used when column A is blank.
 *
 * Six rows of 641 carry no account: February 2 and May 18, plus the four undated August
 * rows that are skipped anyway. They park here, flagged, rather than being filled down
 * from the row above — the blanks are not contiguous, so that would invent data.
 */
export const UNASSIGNED_ACCOUNT = 'unassigned';

/** Column A label → account slug. Every label that actually appears in the ledger. */
export const ACCOUNT_MAP: Record<string, string> = {
  'idfc': 'idfc',
  'sbm': 'sbm',
  'cash': 'cash',
  'icici pers+demat': 'icici-pers-demat',
  'hdfc sha': 'hdfc-sha',
  'fi federal': 'fi-federal',
  'icici pers ta': 'icici-pers-ta',
  'canara': 'canara',
  'kotak': 'kotak',
  'sbi': 'sbi',
  'icici corp': 'icici-corp',
  'hdfc current': 'hdfc-current',
  'equitas': 'equitas',
  'cub': 'cub',
  'iob account / icici sa': 'iob-icici-sa',
  'dcb niyo': 'dcb-niyo',
};

/**
 * Reclassification rules (`CLAUDE.md` §3).
 *
 * A card bill payment is a **transfer**, not an expense (FR-CARD-03); an FD booking is a
 * transfer into an investment account (FR-SAV-05); an EMI is a transfer to the loan.
 * These are what make the app's totals differ from the workbook's, and what produces the
 * corrected column of FR-DASH-05 — so they are declared here in one table, exported, and
 * counted in the import summary rather than buried in a conditional.
 *
 * Matched on the description, lowercased, longest pattern first.
 */
export interface ReclassRule {
  /** Lowercased description patterns, matched as a whole string. */
  match: string[];
  type: TransactionType;
  /** Destination account for a transfer. */
  toAccountId?: string;
  label: string;
}

export const RECLASS_RULES: ReclassRule[] = [
  { label: 'Card payment — IDFC CC TA', match: ['idfc cc', 'idfc cc ta'], type: 'transfer', toAccountId: 'idfc-cc-ta' },
  { label: 'Card payment — SBM CC', match: ['niyo sbm cc', 'sbm cc', 'niyo cc'], type: 'transfer', toAccountId: 'sbm-cc' },
  { label: 'Card payment — ICICI CC Coral', match: ['icici cc coral ta', 'icici cc coral', 'icici cc', 'icic cc'], type: 'transfer', toAccountId: 'icici-cc-coral' },
  { label: 'Card payment — RBL CC', match: ['rbl cc'], type: 'transfer', toAccountId: 'rbl-cc' },
  { label: 'Card payment — HDFC CC Sha', match: ['hdfc cc sha', 'hdfc cc'], type: 'transfer', toAccountId: 'hdfc-cc-sha' },
  { label: 'FD booking', match: ['hdfc fd', 'icici fd', 'fd booking'], type: 'transfer', toAccountId: 'hdfc-fd' },
  // "Car Loan" and "Mahindra Fin LL" are the same vehicle loan and must collapse onto
  // one account, or the EMI double-counts (FR-LOAN-02).
  { label: 'Loan EMI — collapsed', match: ['mahindra fin ll', 'car loan', 'mahindra fin'], type: 'transfer', toAccountId: 'mahindra-car-loan' },
  { label: 'Savings pot — TA Savings', match: ['ta savings'], type: 'transfer', toAccountId: 'ta-savings' },
  { label: 'Savings pot — Jchit', match: ['jchit'], type: 'transfer', toAccountId: 'jchit' },
  { label: 'Savings pot — GRT', match: ['grt pay', 'grt'], type: 'transfer', toAccountId: 'grt-pay' },
];

/**
 * Deliberately NOT reclassified, and why.
 *
 * `CLAUDE.md`'s card-payment column totals ₹23,68,418 across Jan–Jul, and that figure
 * excludes these — so treating them as card payments would break the FR-DASH-05
 * reconciliation the import is measured against. They stay ordinary expenses.
 */
export const NOT_CARD_PAYMENTS = ['amazon pur', 'am pur', 'am pay'];

// ---------------------------------------------------------------------------

/** Excel serial → `YYYY-MM-DD`. Serials below ~2020 are corrupt, not old. */
export function excelSerialToDateKey(serial: number): DateKey | null {
  if (!Number.isFinite(serial)) return null;
  const epoch = Date.UTC(1899, 11, 30);
  const date = new Date(epoch + Math.round(serial) * 86_400_000);
  if (Number.isNaN(date.getTime())) return null;
  const key = date.toISOString().slice(0, 10);
  return isValidDateKey(key) ? key : null;
}

/** A date that parses but cannot be what the user meant. */
export function isImplausibleDate(dateKey: DateKey): boolean {
  return dateKey < '2020-01-01' || dateKey > '2100-01-01';
}

export function mapAccount(label: string | null): { id: string; mapped: boolean } {
  if (!label || label.trim() === '') return { id: UNASSIGNED_ACCOUNT, mapped: false };
  const slug = ACCOUNT_MAP[label.trim().toLowerCase()];
  return slug ? { id: slug, mapped: true } : { id: UNASSIGNED_ACCOUNT, mapped: false };
}

/** Parse column C, which may hold a number or an arithmetic expression (FR-IMP-08). */
export function parseImportedAmount(
  value: string | number | null,
): { paise: Paise; expression: string | null } | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? { paise: Math.round(value * 100), expression: null } : null;
  }

  const text = String(value).trim();
  if (text === '') return null;

  const result = evaluateExpression(text);
  if (!result.ok) return null;
  return { paise: result.paise, expression: result.literal ? null : text };
}

export function findRule(description: string | null): ReclassRule | null {
  if (!description) return null;
  const low = description.trim().toLowerCase();
  if (NOT_CARD_PAYMENTS.includes(low)) return null;

  // Longest pattern first, so "icici cc coral ta" beats "icici cc".
  let best: { rule: ReclassRule; length: number } | null = null;
  for (const rule of RECLASS_RULES) {
    for (const pattern of rule.match) {
      if (low === pattern && (!best || pattern.length > best.length)) {
        best = { rule, length: pattern.length };
      }
    }
  }
  return best?.rule ?? null;
}

/** Key for duplicate detection: same day, account, amount and description. */
function duplicateKey(input: TransactionInput): string {
  return [input.dateKey, input.accountId, input.amountPaise, (input.description ?? '').trim().toLowerCase()].join('|');
}

/**
 * Build the import plan.
 *
 * Nothing is dropped. A row that cannot be imported becomes an issue with its original
 * values attached, so the review queue can show what the sheet actually said (FR-IMP-13).
 */
export function planImport(rows: RawRow[], sheetOfMonth: (sheet: string) => string | null = () => null): ImportPlan {
  const transactions: PlannedTransaction[] = [];
  const issues: ImportIssue[] = [];
  const byMonth: Record<string, number> = {};
  const byRule: Record<string, number> = {};
  const seen = new Map<string, PlannedTransaction>();
  let skipped = 0;
  let crossSheetDates = 0;

  for (const row of rows) {
    const raw = {
      account: row.account,
      description: row.description,
      amount: row.amount === null ? '' : String(row.amount),
      date: row.dateSerial === null ? '' : String(row.dateSerial),
    };
    const issue = (kind: IssueKind, message: string) =>
      issues.push({ kind, sheet: row.sheet, rowNumber: row.rowNumber, message, raw });

    const amount = parseImportedAmount(row.amount);
    if (!amount) {
      issue(row.amount === null ? 'missing-amount' : 'invalid-amount',
        row.amount === null ? 'No amount.' : `Amount "${raw.amount}" could not be read.`);
      skipped += 1;
      continue;
    }

    if (row.dateSerial === null) {
      issue('missing-date', 'No date.');
      skipped += 1;
      continue;
    }

    const dateKey = excelSerialToDateKey(row.dateSerial);
    if (!dateKey) {
      issue('invalid-date', `Date "${raw.date}" could not be read.`);
      skipped += 1;
      continue;
    }
    if (isImplausibleDate(dateKey)) {
      // Kept out of the import rather than filed under 1900 — but reported, not dropped.
      issue('invalid-date', `Date reads as ${dateKey}, which cannot be right.`);
      skipped += 1;
      continue;
    }

    const account = mapAccount(row.account);
    const rule = findRule(row.description);
    const rowIssues: IssueKind[] = [];

    if (!account.mapped) {
      rowIssues.push(row.account ? 'unmapped-account' : 'missing-account');
      issue(row.account ? 'unmapped-account' : 'missing-account',
        row.account ? `Account "${row.account}" is not one of the seeded accounts.` : 'No account on this row.');
    }

    const input: TransactionInput = {
      type: rule?.type ?? 'expense',
      dateKey,
      accountId: account.id,
      toAccountId: rule?.toAccountId ?? null,
      amountPaise: Math.abs(amount.paise),
      amountExpression: amount.expression,
      // FR-IMP-06: the original column B value is preserved verbatim.
      description: row.description?.trim() || null,
      categoryId: null,
      migrated: true,
      needsEnrichment: rowIssues.length > 0,
    };

    // A transfer to the account it came from is not a transfer. Happens when a card
    // payment row carries no source account and both sides resolve to the same slug.
    if (input.type === 'transfer' && input.toAccountId === input.accountId) {
      input.type = 'expense';
      input.toAccountId = null;
    }

    // An EMI is two things at once (FR-LOAN-06): the interest is spending, the principal
    // repays the lender. The split comes from the lender's own schedule, matched on the
    // due date — never derived, because FR-LOAN-04 makes the schedule authoritative.
    //
    // Without this the whole EMI would credit against the loan, and the seven migrated
    // months would show the car ₹65,051 further paid off than it is.
    if (input.type === 'transfer' && input.toAccountId === MAHINDRA_CAR_LOAN.accountId) {
      const split = emiSplitOn(MAHINDRA_SCHEDULE, dateKey);
      if (split && input.amountPaise === MAHINDRA_SCHEDULE[split.instalmentNo - 1].amountPaise) {
        input.loanId = MAHINDRA_CAR_LOAN.id;
        input.instalmentNo = split.instalmentNo;
        input.principalPaise = split.principalPaise;
        input.interestPaise = split.interestPaise;
      } else {
        // A payment that is not a scheduled instalment — an extra payment, or a month
        // paid on the wrong day. Left whole and flagged rather than split against an
        // instalment it may not be.
        rowIssues.push('unmatched-emi');
        issue('unmatched-emi', `No instalment falls due on ${dateKey} for this amount.`);
      }
    }

    const planned: PlannedTransaction = {
      input,
      sheet: row.sheet,
      rowNumber: row.rowNumber,
      issues: rowIssues,
      rule: rule?.label ?? null,
    };

    // FR-IMP-12: duplicates are FLAGGED, never silently skipped. Genuine same-day
    // repeats exist — two Tender coconut rows on 3 Aug at 160 and 80.
    const key = duplicateKey(input);
    const earlier = seen.get(key);
    if (earlier) {
      planned.issues.push('duplicate');
      planned.input.needsEnrichment = true;
      issues.push({
        kind: 'duplicate', sheet: row.sheet, rowNumber: row.rowNumber,
        message: `Identical to ${earlier.sheet} row ${earlier.rowNumber}. Keep both unless you know otherwise.`,
        raw,
      });
    } else {
      seen.set(key, planned);
    }

    // FR-IMP-02: rows import by their own date, not the sheet they sit on.
    const month = dateKey.slice(0, 7);
    byMonth[month] = (byMonth[month] ?? 0) + 1;
    if (sheetOfMonth(row.sheet) && sheetOfMonth(row.sheet) !== month) crossSheetDates += 1;
    if (planned.rule) byRule[planned.rule] = (byRule[planned.rule] ?? 0) + 1;

    transactions.push(planned);
  }

  return {
    transactions,
    issues,
    summary: {
      rowsRead: rows.length,
      importable: transactions.length,
      flagged: transactions.filter((t) => t.issues.length > 0).length,
      skipped,
      byMonth,
      byRule,
      crossSheetDates,
    },
  };
}
